import { JSX, useEffect, useLayoutEffect } from 'react';

import classNames from 'classnames';
import { useLocation } from 'react-router';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';

import { Box } from '@vk-hr-tek/ui/Box';
import { useInject } from '@vk-hr-tek/core/ioc';
import { Idle } from '@vk-hr-tek/core/idle';
import { Logger } from '@vk-hr-tek/core/logger';
import { TokenService } from '@vk-hr-tek/core/http';
import { DownloadNotification } from '@vk-hr-tek/ui/DownloadNotification';
import { InfiniteScrollProvider } from '@vk-hr-tek/ui/InfiniteScroll';
import {
  selectDownloads,
  cancelDownloads,
} from '@vk-hr-tek/core/download-notification';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { useIsDesktop } from '@vk-hr-tek/ui/hooks';
import { useMetaTitles } from '@vk-hr-tek/core/hooks';
import { getTranslations } from '@vk-hr-tek/core/translations';
import { useTheme } from '@vk-hr-tek/ui/Theme';
import { AnalyticService, GoalsEnum } from '@vk-hr-tek/core/analytics';

import { useSelector, useDispatch, useRole, useRedirects } from '@app/hooks';
import { Resources } from '@app/ability';

import { Chat } from '../Chat';
import { useStoreReset } from '../hooks';
import { useAuth } from '../../auth';
import { logout } from '../../auth/slice';
import { AuthRouterService } from '../../auth/services';
import {
  selectIsCertificateReady,
  selectUserRepresentative,
  changeRole,
  selectUser,
  selectStatus,
  getUser,
} from '../../user';
import { Notifications } from '../notifications';
import { ErrorBoundary } from '../error-boundary';
import { Logo } from '../ui/Logo';
import { DisabledLKLayout } from '../DisabledLKLayout';
import { Footer } from '../ui/Footer';
import { getSideMenu, selectServiceUnavailable } from '../../layout/slice';
import { PageError } from '../page';
import { AdblockDetector } from '../AdblockDetector';
import { KonturUnavailableDialog } from '../KonturUnavailableDialog';
import { VK_HEADER_HEIGHT, vkLinks } from '../constants';
import { Header } from '../ui/Header';
import { Sidebar } from '../ui/Sidebar';
import { WithTranslations } from '../WithTranslations';

import { useStyles } from './GeneralLayout.styles';

interface GeneralLayoutProps {
  children: JSX.Element;
  metaTitles: {
    parentResource?: Resources;
    resource?: Resources;
    basename?: string;
    completeTitle?: {
      company: string;
      employee: string;
    };
    href: string;
  }[];
}

window.webim = {
  accountName: window.REACT_APP_VKHRTEK_WEBIM_ACCOUNT_NAME,
  domain: `${window.REACT_APP_VKHRTEK_WEBIM_ACCOUNT_NAME}.webim.ru`,
  location: 'hr_box',
};

export const GeneralLayout = ({ children, metaTitles }: GeneralLayoutProps) => {
  useRedirects();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const isAuthenticated = useAuth();
  const { pathname } = useLocation();
  const authRouter = useInject(AuthRouterService);
  const analyticService = useInject(AnalyticService);
  const logger = useInject(Logger);
  const tokenService = useInject<TokenService>(TokenService);
  const isDesktop = useIsDesktop();

  const theme = useTheme();

  const [role] = useRole();

  const isCertificateReady = useSelector(selectIsCertificateReady);
  const isServiceUnavailable = useSelector(selectServiceUnavailable);
  const userRepresentative = useSelector(selectUserRepresentative);
  const userData = useSelector(selectUser);
  const userStatus = useSelector(selectStatus);
  const notifications = useSelector(selectDownloads);

  const handleCloseDownloadNotification = () => {
    dispatch(cancelDownloads());
  };

  const isLkDisabled =
    window.REACT_APP_VKHRTEK_LK_DISABLED && !userRepresentative;

  const isDashboard = pathname === '/';

  const layoutHeightGap =
    theme === 'vk' && isDesktop ? `${VK_HEADER_HEIGHT}px` : '0px';

  const handleIdle = () => {
    dispatch(logout());
  };

  useMetaTitles(metaTitles, role);

  const { resetStore } = useStoreReset();

  const renderHeader = () => {
    if (isDashboard && !isLkDisabled) {
      return <Box id="dashboard-header" className={classes.dashboardHeader} />;
    } else {
      return <Header />;
    }
  };

  useEffect(() => {
    let timerId: unknown = null;

    if (!isAuthenticated && !pathname.startsWith('/auth/')) {
      timerId = setTimeout(() => authRouter.redirectToLogin(), 0);
    }

    return () => {
      if (timerId && typeof timerId === 'number') {
        clearTimeout(timerId);
      }
    };
  }, [isAuthenticated, pathname, authRouter]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getSideMenu());
      dispatch(getTranslations());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && userStatus === 'idle' && tokenService.get()) {
      dispatch(getUser());
    }
  }, [dispatch, isAuthenticated, userStatus, tokenService]);

  useEffect(() => {
    if (userData?.id) {
      analyticService.pageView(userData.id);
    }
  }, [userData, analyticService]);

  useLayoutEffect(() => {
    dispatch(changeRole(role));
  }, [role, dispatch]);

  const pageMetaTitle =
    typeof document === 'object' &&
    document !== null &&
    !Array.isArray(document) &&
    document?.title
      ? document.title.split('-')
      : [];

  const handleLogAction = (title: string) => {
    analyticService.registerAction(GoalsEnum.openLink, {
      page: pageMetaTitle[pageMetaTitle.length - 1] || '',
      element: 'Боковое меню',
      block: 'Боковое меню',
      actionType: `Переход на страницу ${title}`,
    });
  };

  const onMenuItemClick = (pageTitle: string) => {
    resetStore();
    handleLogAction(pageTitle);
  };

  if (isServiceUnavailable) {
    return (
      <div className={classes.root}>
        <PageError status={503} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <ErrorBoundary logger={logger}>
        <InfiniteScrollProvider target="scrollable-target">
          {isAuthenticated &&
            window.location.hostname ===
              window.REACT_APP_VKHRTEK_ALI_DOMAIN && (
              <Idle onIdle={handleIdle} />
            )}
          {isAuthenticated && <KonturUnavailableDialog />}
          {isAuthenticated && isCertificateReady ? (
            <WithTranslations>
              <AppBar component="div" className={classes.myAppBar}>
                {theme === 'vk' && (
                  <div className={classes.vkHeader}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Box display="flex" alignItems="center">
                        <a
                          href="https://vk.company"
                          rel="noopener noreferrer"
                          target="_blank"
                          className={classes.vkHeaderLogo}
                        >
                          <Icon name="logo_vk_color" size="20" />
                        </a>
                        {vkLinks.map(({ label, url }) => (
                          <a
                            href={url}
                            key={url}
                            rel="noopener noreferrer"
                            target="_blank"
                            className={classes.vkHeaderLink}
                          >
                            {label}
                          </a>
                        ))}
                      </Box>
                      <Box>
                        <a
                          href="https://home.vk.team/pages/page/konfidencialno_VK"
                          rel="noopener noreferrer"
                          target="_blank"
                          className={classes.vkHeaderLink}
                        >
                          Конфиденциально VK
                        </a>
                      </Box>
                    </Box>
                  </div>
                )}
              </AppBar>
              <Box display="flex">
                {isDesktop && !isLkDisabled && (
                  <Box height={`calc(100dvh - ${layoutHeightGap})`}>
                    <Sidebar
                      logo={<Logo inSidebar />}
                      onAction={onMenuItemClick}
                    />
                  </Box>
                )}

                <Box
                  id="scrollable-target"
                  display="flex"
                  flexDirection="column"
                  height={`calc(100dvh - ${layoutHeightGap})`}
                  width="100%"
                  overflow="auto"
                >
                  {renderHeader()}

                  <div
                    id="scrollable-content"
                    className={classNames(classes.main, classes.content)}
                  >
                    <Container disableGutters className={classes.container}>
                      {isLkDisabled ? (
                        <DisabledLKLayout />
                      ) : (
                        <>
                          <AdblockDetector />
                          <Notifications />
                          {children}
                          <DownloadNotification
                            notifications={notifications}
                            onClose={handleCloseDownloadNotification}
                          />
                        </>
                      )}
                    </Container>
                  </div>

                  {window.REACT_APP_VKHRTEK_WEBIM_ACCOUNT_NAME && <Chat />}
                  <Footer />
                </Box>
              </Box>
            </WithTranslations>
          ) : (
            <WithTranslations>{children}</WithTranslations>
          )}
        </InfiniteScrollProvider>
      </ErrorBoundary>
    </div>
  );
};
